import React, { useState } from "react";
import { CalendarDays, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Working days and non-working dates for the project's schedule maths. */
export const PlanCalendarDialog = ({ open, onOpenChange, calendar, onSave }) => {
  const [days, setDays] = useState(calendar?.working_days ?? [0, 1, 2, 3, 4]);
  const [holidays, setHolidays] = useState(calendar?.holidays ?? []);
  const [next, setNext] = useState("");

  const toggle = (i) => setDays((d) => (d.includes(i) ? d.filter((x) => x !== i) : [...d, i].sort()));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="calendar-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Working calendar</DialogTitle>
          <DialogDescription>
            Durations, dependency lags and slack are all counted in working days, so this drives every
            date the planner calculates.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <label className="field-label">Working days</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {DAYS.map((d, i) => (
                <button key={d} data-testid={`calendar-day-${i}`} onClick={() => toggle(i)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                    days.includes(i) ? "bg-[color:var(--brand-primary)] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="field-label">Non-working dates (holidays, shutdowns)</label>
            <div className="mt-1 flex items-center gap-2">
              <Input data-testid="calendar-holiday-input" type="date" value={next} onChange={(e) => setNext(e.target.value)} className="h-9 w-44" />
              <Button size="sm" variant="outline" data-testid="calendar-add-holiday-btn" className="gap-1.5"
                onClick={() => { if (next && !holidays.includes(next)) setHolidays([...holidays, next].sort()); setNext(""); }}>
                <Plus size={13} /> Add
              </Button>
            </div>
            {holidays.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="calendar-holiday-list">
                {holidays.map((h) => (
                  <span key={h} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                    {h}
                    <button data-testid={`remove-holiday-${h}`} onClick={() => setHolidays(holidays.filter((x) => x !== h))} aria-label={`Remove ${h}`} className="text-slate-400 hover:text-red-600"><X size={11} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" data-testid="calendar-cancel-btn" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="calendar-save-btn" className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}
            onClick={() => onSave({ working_days: days, holidays })}>
            <CalendarDays size={14} /> Save calendar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
